/**
 * @file src/views/VID/views/VidStyles.tsx
 * @exports VidBase, VidCenterBase, StepperBox, MainContents, ForceWidth, Title, FormText, LandingContainer, LandingLeftSection, LandingHeading, LandingSubheading, LandingInstruction, LandingBrowserLink, LandingButtonBox, LandingRightSection, LandingPhoneMockup, WelcomeLogoBox, WelcomeTitle, TimerGroup, TimerIconBox, InstructionsHeader, InstructionsGroup, InstructionBox, IconLoader, TextBox, TextBoxStacked, InstructionText, MessageBox, MessageTitle, ListBox, List, FormContainer, ToggleButtonWrapper, IdData, IdDataSection, ReadOnlyLabel, FormBox, RescanBox, RescanButtonBox, SsnButtonWrapper, NoSsnBox, NoSsnText, SubHeading, HiddenSubmit, CenterBoxWrapper, CenterBox, LogoBox, SuccessTitle, SuccessMessage, ContentWrapper, SignatureBox, SignatureWrapper, TrashIconWrapper, InputWrapper, Spacer, Line, SignatureCross, InfoSection, UL, SelfieInputWrapper, PreviewWrapper, Img, MessageCenterBox, MessageContainer, MessageIconWrapper, MessageHeader, IconBadgeOverlay, SkinnyIconBadgeOverlay, MessageIconBox, MessageBodyText, MessageActions
 * @summary styled-components for AppErrorFallback, PreviewImage, VidAdditionalInformation, VidAlmostDone, ServiceUnavailable, VidExpired, VidIdType, VidIdUpload, VidInternationalIdType, VidLanding, VidManualEntry, VidPersonalInformation, VidSelfie, VidSignDisclosures, VidSsn, VidSuccess, VidUsIdType, VidWelcome, getIdUploadFields components
 */

import {
	BodyLEmphasis,
	BodyMEmphasis,
	BodyMPrimary,
	type ButtonType,
	HeadingH1,
	SubheadingSEmphasis,
	colors,
	loadingPseudo,
} from "@cerebruminc/cerebellum";
import { styled } from "styled-components";
import { HEADER_HEIGHT } from "./../components/VidHeader";

// NOTE: Decrease this once the stepper is installed
export const NO_STEPPER_BREAKPOINT = 900;

// -------- General --------
export const VidBase = styled.div<{ $fullWidth?: boolean; $addIframePadding?: boolean }>`
  display: ${({ $fullWidth }) => ($fullWidth ? "flex" : "grid")};
  grid-template-columns: [stepper-start] 330px [content-start] 1fr [content-end];
  gap: 63px;
  justify-content: center;
  margin: 0 auto;
  max-width: 1350px;
  min-height: calc(100vh - ${HEADER_HEIGHT}px);
  padding: 80px 20px; // No iframe padding needed, since we have over 65px already
  width: 100%;
  @media all and (max-width: ${NO_STEPPER_BREAKPOINT}px) {
    display: flex;
  }
  @media all and (max-width: 738px) {
    max-width: 500px;
  }
  @media all and (max-width: 700px) {
    padding: 30px 5% ${({ $addIframePadding }) => ($addIframePadding ? "95px" : "30px")};
  }
`;
export const VidCenterBase = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - ${HEADER_HEIGHT}px);
  padding: 57px 0;
  @media all and (max-width: 700px) {
    padding: 57px 8%;
  }
`;
export const StepperBox = styled.div`
  grid-column-start: stepper-start;
  grid-column-end: content-start;
  height: 367px;
  position: sticky;
  top: 50px;
  width: 330px;
  @media all and (max-width: ${NO_STEPPER_BREAKPOINT}px) {
    display: none;
  }
`;
export const MainContents = styled.div<{ $maxWidth?: number }>`
  display: flex;
  flex-direction: column;
  grid-column-start: content-start;
  grid-column-end: content-end;
  margin-top: 21px;
  max-width: ${({ $maxWidth }) => $maxWidth || 780}px;
  min-width: 0px;
  width: 100%;
  @media all and (max-width: ${NO_STEPPER_BREAKPOINT}px) {
    margin: 0 auto;
  }
`;
export const ForceWidth = styled.div<{ $width?: number }>`
  max-width: 100%;
  width: ${({ $width }) => $width || 780}px;
`;
export const Title = styled(HeadingH1)<{ $smallText?: boolean }>`
  margin: 20px 0 14px;
  @media all and (min-width: 750px) {
    ${({ $smallText }) => ($smallText ? "font-size: 24px;" : "")}
  }
  @media all and (min-width: 901px) and (max-width: 950px) {
    ${({ $smallText }) => ($smallText ? "font-size: 24px;" : "")}
  }
`;
export const FormText = styled(BodyMPrimary)`
  margin-bottom: 25px;
  max-width: 100%;
`;

// -------- Landing --------

export const LandingContainer = styled.div<{ $highContrast?: boolean }>`
  background: ${({ $highContrast }) => ($highContrast ? colors.WHITE : "linear-gradient(180.71deg, #5377ec 7.06%, #c0aaff 50.18%, #ffffff 94.53%)")};
  display: flex;
  justify-content: space-between;
  min-height: 100vh;
  width: 100%;
`;

export const LandingLeftSection = styled.div<{ $highContrast?: boolean }>`
  align-items: center;
  color: ${({ $highContrast }) => ($highContrast ? colors.BLACK : "#fff")};
  display: flex;
  flex-direction: column;
  padding: 60px;
  text-align: center;
  width: 50%;

  @media all and (max-width: 900px) {
    width: 100%;
  }
`;

export const LandingHeading = styled.h1`
  font-size: 50px;
  font-weight: 800;
  line-height: 60px;
  margin-bottom: 50px;
  margin-top: 30px;

  @media all and (max-width: 900px) {
    font-size: 40px;
    line-height: 50px;
  }
`;

export const LandingSubheading = styled.h2`
  font-size: 30px;
  font-weight: 700;
  line-height: 35px;
  margin-bottom: 5px;
`;

export const LandingInstruction = styled.p`
  font-size: 16px;
`;

export const LandingBrowserLink = styled.p`
  color: #000;
  font-size: 14px;
  line-height: 20px;
  margin-bottom: 30px;

  button {
    background: none;
    border: none;
    color: inherit;
    cursor: pointer;
    font: inherit;
    padding: 0;
    text-decoration: underline;
  }
`;

export const LandingButtonBox = styled.div<{ $highContrast?: boolean }>`
  align-items: center;
  background-color: #fff;
  border: ${({ $highContrast }) => ($highContrast ? `1px solid ${colors.BLACK}` : "none")};
  border-radius: 10px;
  color: #000;
  display: flex;
  font-size: 16px;
  justify-content: space-between;
  margin-bottom: 20px;
  padding: 16px;
  text-align: left;
  width: 100%;
`;

export const LandingRightSection = styled.div`
  align-items: center;
  background-color: white;
  display: flex;
  justify-content: center;
  min-height: 100vh;
  width: 50%;

  @media all and (max-width: 900px) {
    display: none;
  }
`;

export const LandingPhoneMockup = styled.img`
  height: auto;
  width: 80%;
`;

// -------- Welcome --------
export const WelcomeLogoBox = styled.div`
  height: 88px;
  width: 88px;
  overflow: hidden;
`;
export const WelcomeTitle = styled.h1`
  color: ${colors.BLACK};
  font-size: 40px;
  margin: 18px 0 24px;
  text-align: center;
`;
export const TimerGroup = styled.div<{ $highContrast?: boolean }>`
  align-items: center;
  background-color: ${({ $highContrast }) => ($highContrast ? colors.WHITE : colors.COOL_GREY_4)};
	border: ${({ $highContrast }) => ($highContrast ? `1px solid ${colors.BLACK}` : "none")};
  border-radius: 17px;
  display: flex;
  gap: 12px;
  justify-content: center;
  height: 34px;
  margin-bottom: 24px;
  width: 162px;
`;
export const TimerIconBox = styled.div`
  height: 19px;
  width: 19px;
`;
export const InstructionsHeader = styled(BodyLEmphasis)<{ $loading?: boolean }>`
  text-align: center;
  line-height: 28px;

  ${({ $loading }) =>
		$loading &&
		loadingPseudo({
			topOffset: 9,
			bgColor: colors.COOL_GREY_4,
			loadingColor: colors.COOL_GREY_15,
			borderRadius: 6,
		})}
`;

export const InstructionsGroup = styled.div`
  margin: 38px 0 60px;
  max-width: 535px;
  width: 90%;
  @media screen and (max-width: 319px) {
    width: 100%;
  }
`;
export const InstructionBox = styled.div`
  align-items: center;
  display: flex;
  gap: 25px;
  min-height: 91px;
  @media screen and (max-width: 400px) {
    gap: 20px;
  }
`;
export const IconLoader = styled.div<{ $isLoading: boolean }>`
  position: relative;
  ${({ $isLoading }) =>
		$isLoading &&
		loadingPseudo({
			topOffset: 0,
			bgColor: colors.COOL_GREY_4,
			loadingColor: colors.COOL_GREY_15,
			borderRadius: 50,
		})}
`;
export const TextBox = styled.div<{ $highContrast?: boolean }>`
  align-items: center;
  border-bottom: 1px solid ${({ $highContrast }) => ($highContrast ? colors.BLACK : colors.COOL_GREY_20)};
  display: flex;
  justify-content: space-between;
  min-height: 91px;
  padding: 25px 0;
  flex: 1 0;
  gap: 5px;
  & * > {
    flex: 1 0;
  }
  @media screen and (max-width: 319px) {
    gap: 0;
  }
`;
export const TextBoxStacked = styled(TextBox)<{ $stacked: boolean }>`
  align-items: ${({ $stacked }) => ($stacked ? "flex-start" : "center")};
  flex-direction: ${({ $stacked }) => ($stacked ? "column" : "row")};
  gap: 2px;
`;
export const InstructionText = styled(BodyLEmphasis)<{ $isLoading: boolean }>`
  line-height: 24px;
  flex: ${({ $isLoading }) => ($isLoading ? 0.9 : 1)} 1;
  max-height: ${({ $isLoading }) => ($isLoading ? "24px" : "none")};
  ${({ $isLoading }) =>
		$isLoading &&
		loadingPseudo({
			topOffset: 4,
			bgColor: colors.COOL_GREY_4,
			loadingColor: colors.COOL_GREY_15,
			borderRadius: 6,
		})}
  span {
    color: ${({ $isLoading }) => ($isLoading ? "transparent" : "inherit")};
  }
`;

// -------- ID Upload --------
export const MessageBox = styled.div<{ $highContrast?: boolean }>`
  border: 1px solid ${({ $highContrast }) => ($highContrast ? colors.BLACK : colors.BLUE_100)};
  border-radius: 10px;
  background-color: ${({ $highContrast }) => ($highContrast ? colors.WHITE : colors.BLUE_5)};
  padding: 25px 32px 35px;
  max-width: 530px;
  margin: 10px 0 30px 0;
  @media screen and (max-width: 319px) {
    padding: 25px 25px 35px;
  }
`;

export const MessageTitle = styled(BodyMEmphasis)<{ $highContrast?: boolean }>`
  color: ${({ $highContrast }) => ($highContrast ? colors.BLACK : colors.BLUE_120)};
`;

export const ListBox = styled.div`
  padding-left: 17px;
  margin-top: 11px;
`;

export const List = styled.ul`
  list-style-type: disc;
  margin: 0;
  padding: 0;

  li {
    line-height: 24px;
    padding-left: 10px;
  }
`;
export const FormContainer = styled.div<{ $narrow?: boolean }>`
  max-width: ${({ $narrow }) => ($narrow ? 550 : 1000)}px;
`;

export const ToggleButtonWrapper = styled.div`
  margin-top: 16px;
  margin-bottom: 40px;
`;

// -------- Personal Information --------
export const IdData = styled.div`
  margin-top: 29px;
  margin-bottom: 39px;
`;
export const IdDataSection = styled.div<{ $highContrast?: boolean }>`
  background-color: ${({ $highContrast }) => ($highContrast ? colors.WHITE : colors.COOL_GREY_4)};
  border-radius: 10px;
	border: ${({ $highContrast }) => ($highContrast ? `1px solid ${colors.BLACK}` : "none")};
  padding: 20px 24px;
  margin-bottom: 7px;
`;
export const ReadOnlyLabel = styled(SubheadingSEmphasis)<{ $highContrast: boolean }>`
	// This needs to be BLUE_120 to be accessible, since it's on a grey background
	color: ${({ $highContrast }) => ($highContrast ? colors.BLACK : colors.BLUE_120)};
  margin-bottom: 6px;
`;
export const FormBox = styled.div<{ $allowAlias?: boolean }>`
  margin-top: ${({ $allowAlias }) => ($allowAlias ? "20px" : "0px")};
`;
export const RescanBox = styled.div<{ $allowAlias?: boolean; $highContrast?: boolean }>`
  padding-bottom: 40px;
  border-bottom: 1px solid ${({ $highContrast }) => ($highContrast ? colors.BLACK : colors.COOL_GREY_20)};
  margin-bottom: ${({ $allowAlias }) => ($allowAlias ? "32px" : "0px")};
`;
export const RescanButtonBox = styled.div`
  margin-top: 15px;
`;

// -------- SSN ---------
export const SsnButtonWrapper = styled.div`
  display: block;
  margin-bottom: 50px;
  margin-top: -65px;
`;

export const NoSsnBox = styled.div<{ $highContrast?: boolean }>`
  background-color: ${({ $highContrast }) => ($highContrast ? colors.WHITE : "#f6f8fb")};
	border: ${({ $highContrast }) => ($highContrast ? `1px solid ${colors.BLACK}` : "none")};
  padding: 24px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 40px 0 70px 0;
`;

export const NoSsnText = styled.div`
  text-align: left;
`;

export const SubHeading = styled(SubheadingSEmphasis)`
  margin-bottom: 7px;
`;
export const HiddenSubmit = styled.div<ButtonType>`
  display: none;
`;

// -------- Success --------
export const CenterBoxWrapper = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  max-width: 100%;
  min-height: calc(100vh - ${HEADER_HEIGHT}px - 57 * 2px);
  min-width: 0;
  padding-top: 30px;
  width: 100%;
`;

export const CenterBox = styled.div`
  box-sizing: border-box;
  max-width: 100%;
  min-width: 0;
  padding-bottom: ${HEADER_HEIGHT}px;
  text-align: center;
  width: 100%;
`;

export const LogoBox = styled.div<{ $size?: number }>`
  width: ${({ $size }) => $size ?? 65}px;
  height: ${({ $size }) => $size ?? 65}px;
  overflow: hidden;
  margin: 0 auto;
  margin-bottom: 26px;
`;

export const SuccessTitle = styled(WelcomeTitle)`
  font-weight: 600;
`;

export const SuccessMessage = styled.p<{ $highContrast?: boolean }>`
  color: ${({ $highContrast }) => ($highContrast ? colors.BLACK : colors.COOL_GREY_65)};
  font-size: 15px;
  font-weight: 600;
  line-height: 20px;
  margin: 85px auto 0;
  max-width: 430px;
  text-align: center;
  width: 100%;
`;

// -------- Disclosures --------
export const ContentWrapper = styled(MainContents)`
  width: 100%;
`;

export const SignatureBox = styled.div<{ $highContrast?: boolean }>`
  background-color: ${({ $highContrast }) => ($highContrast ? colors.WHITE : colors.BLUE_5)};
  border: ${({ $highContrast }) => ($highContrast ? `1px solid ${colors.BLACK}` : "none")};
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  max-width: 100%;
  padding: 26px;
  position: relative;
  width: 691px;
`;

export const SignatureWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

export const TrashIconWrapper = styled.div`
  position: absolute;
  top: 33px;
  right: 37px;
`;
export const InputWrapper = styled.div`
  width: 691px;
  max-width: 100%;
`;
export const Spacer = styled.div<{ $padding?: string; $margin?: string }>`
  padding: ${(props) => (props.$padding ? props.$padding : "0px")};
  margin: ${(props) => (props.$margin ? props.$margin : "0px")};
`;

export const Line = styled.div<{ $highContrast?: boolean }>`
  width: calc(100% - 143px);
  position: absolute;
  left: 77px;
  bottom: 99px;
  height: 1px;
  background-color: ${({ $highContrast }) => ($highContrast ? colors.BLACK : colors.COOL_GREY_50)};

  @media (max-width: 426px) {
    width: calc(100% - 100px);
    left: 50px;
    bottom: 80px;
  }
`;

export const SignatureCross = styled.div`
  position: absolute;
  left: 50px;
  bottom: 140px;
  width: 15px;
  height: 15px;

  @media (max-width: 426px) {
    bottom: 120px;
  }
`;

export const InfoSection = styled.div<{ $highContrast?: boolean }>`
  background: ${({ $highContrast }) => ($highContrast ? colors.WHITE : colors.BLUE_5)};
  border: 1px solid ${({ $highContrast }) => ($highContrast ? colors.BLACK : colors.BLUE_100)};
  border-radius: 10px;
  padding: 26px 34px;
  margin-bottom: 36px;
  max-width: 529px;
`;

export const UL = styled.ul`
  list-style-type: disc;
  padding: 10px 16px;

  > li {
    padding: 5px 10px;
  }
`;

export const SelfieInputWrapper = styled.div<{ $highContrast?: boolean }>`
  align-items: center;
  border-radius: 5px;
  border: 1px solid ${({ $highContrast }) => ($highContrast ? colors.BLACK : "#cfd6e3")};
  display: flex;
  flex-direction: column;
  height: 416px;
  justify-content: space-between;
	margin-bottom: 37px;
  opacity: 1;
  padding-bottom: 32px;
  position: relative;
  width: 320px;

  @media (max-width: 400px) {
    width: 90%;
  }
`;
export const PreviewWrapper = styled.div`
  margin: 10px;
`;
export const Img = styled.img`
  border-radius: 5px;
  border: 1px solid #979797;
  max-height: 500px;
  max-width: 500px;
  object-fit: cover;
  width: 100%;
`;

// ---- Message screens (Expired, Almost Done, etc.) ----
export const MessageCenterBox = styled(CenterBox)`
  max-width: 100%;
  width: 600px;
`;
export const MessageContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: center;
`;
export const MessageIconWrapper = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  margin-bottom: 50px;
  position: relative;
`;
export const MessageHeader = styled.h1`
  color: black;
  font-size: 40px;
  font-weight: 700;
  line-height: 40px;
  margin-bottom: 24px;
  @media (max-width: 510px) {
    font-size: 30px;
    line-height: 30px;
  }
`;
export const IconBadgeOverlay = styled.div`
  align-items: center;
  bottom: -25px;
  display: flex;
  height: 54px;
  justify-content: center;
  left: 50%;
  padding: 8px;
  position: absolute;
  right: 50%;
  transform: translateX(-50%);
  width: 54px;
  z-index: 10;
  &::before {
    background-color: white;
    content: '';
    height: 25px;
    position: absolute;
    width:30px;
  }
`;
export const SkinnyIconBadgeOverlay = styled(IconBadgeOverlay)`
  &::before {
    width: 8px;
  }
`;
export const MessageIconBox = styled.div<{ $highContrast?: boolean }>`
  align-items: center;
  background-color: ${({ $highContrast }) => ($highContrast ? colors.BLACK : colors.COOL_GREY_30)};
  border-radius: 100%;
  display: flex;
  height: 90px;
  justify-content: center;
  padding: 8px;
  width: 90px;
`;
export const MessageBodyText = styled.p`
  color: black;
  font-size: 18px;
  line-height: 25px;
  margin-bottom: 30px;
`;
export const MessageActions = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 10px;
`;
